import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch } from '@nimbitlabs/ui';
import { useSubscriptionStore } from '../subscriptions/subscriptionStore';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  department?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InviteUserDto {
  email: string;
  name?: string;
  role: string;
  department?: string;
}

export interface UpdateUserDto {
  name?: string;
  role?: string;
  department?: string;
  status?: string;
}

interface UsersState {
  users: User[];
  loading: boolean;
  error: string | null;
  
  fetchUsers: () => Promise<void>;
  fetchUser: (id: string) => Promise<User>;
  inviteUser: (data: InviteUserDto) => Promise<void>;
  updateUser: (userId: string, data: UpdateUserDto) => Promise<void>;
  removeUser: (userId: string) => Promise<void>;
  updateUserProfile: (data: { name?: string }) => Promise<void>;
  hasReachedLimit: () => boolean;
  getUserLimit: () => { current: number; limit: number | null; hasReachedLimit: boolean };
  clearError: () => void;
  reset: () => void;
}

export const useUsersStore = create<UsersState>()(
  subscribeWithSelector((set, get) => ({
    users: [],
    loading: false,
    error: null,
  
  fetchUsers: async () => {
    set({ loading: true, error: null });
    try {
      const response = await authorizedFetch('/accounts/users');
      const users = await response.json();
      set({ users, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch users', loading: false });
    }
  },
  
  fetchUser: async (id: string) => {
    try {
      const response = await authorizedFetch(`/accounts/users/${id}`);
      const user = await response.json();
      set(state => ({
        users: state.users.some(u => u.id === id)
          ? state.users.map(u => u.id === id ? user : u)
          : [...state.users, user]
      }));
      return user;
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch user' });
      throw e;
    }
  },
  
  fetchPendingInvitations: async () => {
    set({ loading: true, error: null });
    try {
      const response = await authorizedFetch('/accounts/users/invitations/pending');
      const pendingInvitations = await response.json();
      set({ pendingInvitations, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch pending invitations', loading: false });
    }
  },
  
  inviteUser: async (data: InviteUserDto) => {
    try {
      const response = await authorizedFetch('/users/invite', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to invite user' }));
        throw new Error(error.error || 'Failed to invite user');
      }
      
      await get().fetchUsers();
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to invite user' });
      throw e;
    }
  },
  
  updateUser: async (userId: string, data: UpdateUserDto) => {
    try {
      const response = await authorizedFetch(`/accounts/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      const updatedUser = await response.json();
      set(state => ({
        users: state.users.map(u => u.id === userId ? updatedUser : u)
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to update user' });
      throw e;
    }
  },
  
  removeUser: async (userId: string) => {
    try {
      const response = await authorizedFetch(`/accounts/users/${userId}`, {
        method: 'DELETE',
      });
      await response.json();
      set(state => ({
        users: state.users.filter(u => u.id !== userId)
      }));
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to remove user' });
      throw e;
    }
  },
  
  updateUserProfile: async (data: { name?: string }) => {
    try {
      const response = await authorizedFetch('/user/profile', {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      await response.json();
      await get().fetchUsers();
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to update profile' });
      throw e;
    }
  },
  
  hasReachedLimit: () => {
    const { limits, usage } = useSubscriptionStore.getState();
    if (!limits || !usage) return true; // No data = assume limit reached
    const userLimit = limits.features?.users?.limit;
    if (userLimit === undefined) return false; // No limit = never reached
    return usage.users >= userLimit;
  },
  
  getUserLimit: () => {
    const { limits, usage } = useSubscriptionStore.getState();
    const current = usage?.users ?? 0;
    const limit = limits?.features?.users?.limit ?? null;
    const hasReachedLimit = limit !== null && current >= limit;
    return { current, limit, hasReachedLimit };
  },
  
  clearError: () => set({ error: null }),
  
  reset: () => set({ users: [], loading: false, error: null }),
})));

// Hook to use users
export function useUsers() {
  const users = useUsersStore((state) => state.users);
  const loading = useUsersStore((state) => state.loading);
  const fetchUsers = useUsersStore((state) => state.fetchUsers);
  
  return {
    users,
    loading,
    fetchUsers,
  };
}

// Note: Users store is INDEPENDENT - not tied to account changes
// Users are fetched based on auth context, not account selection
// Components should call fetchUsers() explicitly when needed
